import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlassMartiniAlt,
  faSearch,
  faBookmark,
  faUserCircle,
  faRandom,
  faStar,
  faStarHalfAlt,
} from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import Header from './components/Header';

const IMAGE_BASE_URL = '/img/cocktails/';

function HomePage({ userData, onLogout, apiClient }) {
  const navigate = useNavigate();
  const [featuredCocktail, setFeaturedCocktail] = useState(null);
  const [surpriseMeDrinkId, setSurpriseMeDrinkId] = useState(null);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const data = await apiClient.request('/api/home', {
          method: 'POST',
          body: JSON.stringify({ userid: userData.userid }),
        });
        setFeaturedCocktail(data.featured_cocktail);
        setSurpriseMeDrinkId(data.surprise_me_drinkid);
        setRecentlyViewed(data.recently_viewed);
      } catch (err) {
        setError(err.message || 'Failed to fetch home data');
        if (err.message === 'Failed to refresh token') {
          onLogout(); // Trigger logout if token refresh fails
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };

    if (userData && userData.userid && userData.access_token) {
      fetchHomeData();
    } else {
      setError('User data is incomplete');
      setLoading(false);
    }
  }, [userData, apiClient, onLogout, navigate]);

  const handleListsClick = () => {
    navigate('/lists');
  };
  const handleMyBarClick = () => {
    navigate('/my-bar');
  };
  const handleSurpriseMeClick = () => {
    if (surpriseMeDrinkId) {
      navigate(`/cocktail/${surpriseMeDrinkId}`);
    }
  };

  const handleCocktailClick = (drinkid) => {
    navigate(`/cocktail/${drinkid}`);
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FontAwesomeIcon key={i} icon={faStar} className="text-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<FontAwesomeIcon key={i} icon={faStarHalfAlt} className="text-yellow-400" />);
      } else {
        stars.push(<FontAwesomeIcon key={i} icon={faStarRegular} className="text-yellow-400" />);
      }
    }
    return stars;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="home-container">
      <div className="home-gradient-bg"></div>
      <div className="home-content-container">
        {/* Header */}
        {/* <header className="header">
          <div>
            <h1 className="app-title">Good-Call</h1>
            <p className="subtitle">Find your perfect mix</p>
          </div>
          <button className="profile-btn">
            <FontAwesomeIcon icon={faUserCircle} />
          </button>
        </header> */}
        <Header showBack={false} title="Good-Call" subtitle="Find your perfect mix" showProfile={true} />

        {/* Search Section */}
        <div className="search-section">
          <div className="search-bar">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search cocktails or ingredients"
              className="search-input"
            />
          </div>
          <div className="category-pills">
            <button className="category-pill active">Popular</button>
            <button className="category-pill">Spirit-based</button>
            <button className="category-pill">Fruity</button>
            <button className="category-pill">Classic</button>
            <button className="category-pill">Modern</button>
          </div>
        </div>

        {/* Featured Cocktails */}
        {featuredCocktail && (
          <section className="featured-section">
            <div className="section-sub-header">
              <h2>Featured Cocktails</h2>
              <a href="#" className="see-all">See all</a>
            </div>
            <div
              className="featured-card"
              onClick={() => handleCocktailClick(featuredCocktail.drinkid)}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={`${IMAGE_BASE_URL}${featuredCocktail.image}`}
                alt={featuredCocktail.name}
                className="featured-img"
              />
              <div className="featured-overlay"></div>
              <div className="featured-content">
                <span className="staff-pick">Staff Pick</span>
                <h3>{featuredCocktail.name}</h3>
                <p>{featuredCocktail.description}</p>
              </div>
              <button
                className="bookmark-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon
                  icon={faBookmark}
                  className={featuredCocktail.in_lists.length > 0 ? 'text-primary' : 'text-white'}
                />
              </button>
            </div>
          </section>
        )}

        {/* Discover by Method */}
        <section className="discover-section">
          <h2>Discover by Method</h2>
          <div className="discover-grid">
            <div className="recipe-card" onClick={() => navigate('/spirit')} style={{ cursor: 'pointer' }}>
              <div className="icon-box">
                <FontAwesomeIcon icon={faGlassMartiniAlt} />
              </div>
              <h3>By Spirit</h3>
              <p>Gin, Vodka, Rum...</p>
            </div>
            <div
              className="recipe-card"
              onClick={handleListsClick}
              style={{ cursor: 'pointer' }}
            >
              <div className="icon-box">
                <FontAwesomeIcon icon={faBookmark} />
              </div>
              <h3>Lists</h3>
              <p>Favorites & Lists</p>
            </div>
            <div 
                className="recipe-card"
                onClick={handleMyBarClick}
                style={{ cursor: 'pointer'}}
            >
              <div className="icon-box">
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <h3>What's in My Bar</h3>
              <p>Use what you have</p>
            </div>
            <div
              className="recipe-card"
              onClick={handleSurpriseMeClick}
              style={{ cursor: 'pointer' }}
            >
              <div className="icon-box">
                <FontAwesomeIcon icon={faRandom} />
              </div>
              <h3>Surprise Me</h3>
              <p>Random selection</p>
            </div>
          </div>
        </section>

        {/* Recently Viewed */}
        <section className="recently-viewed">
          <div className="flex justify-between items-center mb-4">
            <h2>Recently Viewed</h2>
            <a href="#" className="see-all">See all</a>
          </div>
          <div className="recently-viewed-list">
            {recentlyViewed.map((cocktail) => (
              <div
                key={cocktail.drinkid}
                className="card recently-viewed-card rounded-xl p-3 flex"
                onClick={() => handleCocktailClick(cocktail.drinkid)}
                style={{ cursor: 'pointer' }}
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden mr-3 flex-shrink-0">
                  <img
                    src={`${IMAGE_BASE_URL}${cocktail.image}`}
                    alt={cocktail.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium mb-1">{cocktail.name}</h3>
                  <div className="flex items-center">
                    <div className="flex text-gold-stars text-sm mr-2">
                      {renderStars(parseFloat(cocktail.rating))}
                    </div>
                    <span className="text-xs text-gray-300">{cocktail.rating}</span>
                  </div>
                  <p className="text-xs text-gray-300 mt-1">{cocktail.short_ingredients}</p>
                </div>
                <button
                  className="ml-2 flex-shrink-0 w-9 h-9 rounded-full bg-dark-800 flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FontAwesomeIcon
                    icon={faBookmark}
                    className={cocktail.in_lists.length > 0 ? 'text-primary' : 'text-gray-400'}
                  />
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default HomePage;